<template>
    <div
        v-if="showQuickView"
        class="popup-wrapper"
        data-close
        @click="hideQuickView"
    >
        <div
            class="popup popup--quick-view"
            @click.stop
        >
            <button
                class="popup__close close-btn xs font-semibold flex items-center"
                data-close
                @click="hideQuickView"
            >
                Close
                <CloseIcon />
            </button>

            <div class="quick-view-inner">
                <div class="quick-view">
                    <div class="product__gallery product__gallery--loading">
                        <div class="product__gallery-inner">
                            <div class="product__gallery-main-image">
                                <img
                                    :src="quickViewProduct.quickViewImage"
                                    :alt="quickViewProduct.title"
                                    width="293"
                                    height="293"
                                >
                            </div>
                        </div>
                    </div>

                    <div class="product-quick-view__information">
                        <div>
                            <h2 class="product__title h4">
                                {{ quickViewProduct.title }}
                            </h2>
                    
                            <div class="product__price-line flex items-center">
                                <p class="product__price">
                                    {{ price }}
                                    <s v-if="compareAtPrice">
                                        {{ compareAtPrice }}
                                    </s>
                                </p>
                                <YotpoStars
                                    ref="yotpoStars"
                                    :product="yotpoProduct"
                                />
                            </div>

                            <div
                                v-if="badges.length > 0"
                                class="product__badges flex flex-wrap"
                            >
                                <div
                                    v-for="badge in badges"
                                    :key="badge"
                                    class="product-badge"
                                >
                                    {{ badge }}
                                </div>
                            </div>

                            <template v-if="quickViewProduct.variants.length > 1">
                                <label
                                    class="product__label"
                                    for="product-quick-view-variant"
                                >
                                    Choose your {{ quickViewProduct.options[0] }}:
                                </label>

                                <v-select
                                    v-model="selectedVariant"
                                    :options="quickViewProduct.variants"
                                    :clearable="false"
                                    :searchable="false"
                                    class="quick-view__variant-dropdown"
                                >
                                    <template #selected-option="option">
                                    <div class="quick-view__variant-dropdown-selected flex items-center sm font-semibold capitalize">
                                        <div class="quick-view__variant-dropdown-image">
                                            <img
                                                :src="option.thumbnailImage"
                                                :alt="option.title"
                                                width="44"
                                                height="44"
                                                class="w-full h-full object-contain"
                                            >
                                        </div>
                                        {{ option.title.split(' / ')[0].toLowerCase() }}
                                        <ChevronRightIcon class="ml-auto" />
                                    </div>
                                    </template>

                                    <template #option="option">
                                        <div
                                            class="quick-view__variant-dropdown-item flex items-center xs capitalize"
                                            :class="{
                                                'quick-view__variant-dropdown-item--oos': option.inventoryQuantity <= 0
                                            }"
                                        >
                                            <div class="quick-view__variant-dropdown-item-image">
                                                <img
                                                    :src="option.thumbnailImage"
                                                    :alt="option.title"
                                                    width="44"
                                                    height="44"
                                                    class="w-full h-full object-contain"
                                                >
                                            </div>
                                            {{ option.title.split(' / ')[0].toLowerCase() }}
                                        </div>
                                    </template>
                                </v-select>
                            </template>

                            <product-selling-plans
                                :quick-view="true"
                                :quick-view-product="quickViewProduct"
                                :quick-view-selected-variant="selectedVariant"
                                :titles="{
                                    'subscribe': 'Subscribe & Save',
                                    'frequency': 'Delivery Frequency:'
                                }"
                            />

                            <product-add-actions
                                ref="mainAddActions"
                                :quick-view="true"
                                :quick-view-product="quickViewProduct"
                                :quick-view-selected-variant="selectedVariant"
                                :quick-view-price="price"
                            />

                            <div
                                v-if="quickViewProduct.metafields.offerInformation"
                                class="offer-information"
                            >
                                <p v-html="quickViewProduct.metafields.offerInformation.children[0].children[0].value" />
                            </div>

                            <product-accordion
                                :titles="accordionTitles"
                                :quick-view="true"
                                :quick-view-product="quickViewProduct"
                                :quick-view-selected-variant="selectedVariant"
                            />

                            <a
                                :href="`/products/${quickViewProduct.handle}`"
                                class="product__view-product-btn btn"
                            >
                                View Full Product
                            </a>
                        </div>
                    </div>
                </div>

                <div 
                    class="product-quick-view__sticky-atc"
                    :class="{
                        'product-quick-view__sticky-atc--hide': isMainAddActionsVisible || !isQuickViewReady
                    }"
                >
                    <product-add-actions
                        :quick-view="true"
                        :quick-view-product="quickViewProduct"
                        :quick-view-selected-variant="selectedVariant"
                        :quick-view-price="price"
                    />
                </div>
            </div>
        </div>


        <back-in-stock-popup
            :quick-view-selected-variant="selectedVariant"
            @click.stop
        />
    </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useCollectionStore } from '../pinia/CollectionStore'
import {productPrice} from '../helpers/product'
import ChevronRightIcon from '../icons/chevron-right'
import ProductAccordion from '../components-bundle/components-product/product-accordion'
import productAddActions from '../components-bundle/components-product/product-add-actions'
import ProductSellingPlans from '../components-bundle/components-product/product-selling-plans'
import YotpoStars from './product-yotpo-stars'
import BackInStockPopup from '../components-bundle/components-product/back-in-stock-popup'
import { getSizedImageUrl } from '@shopify/theme-images'

import CloseIcon from '../icons/close-icon'

export default {
  components: {
    CloseIcon,
    ChevronRightIcon,
    productAddActions,
    ProductAccordion,
    YotpoStars,
    ProductSellingPlans,
    BackInStockPopup
  },
  props: {
    accordionTitles: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      selectedVariant: null,
      isMainAddActionsVisible: true,
      isQuickViewReady: false
    }
  },
  computed: {
    ...mapState(useCollectionStore, ['quickViewProduct', 'showQuickView']),

    yotpoProduct () {
        if (this.quickViewProduct.isCombinedListingsProduct) {
            return this.quickViewProduct.variants.find(variant => variant.id === this.selectedVariant.id).product
        } else {
            return this.quickViewProduct
        }
    },

    priceUnformatted() {
        return this.selectedVariant.price
    },
    price() {
      return productPrice(this.priceUnformatted)
    },
    compareAtPrice() {
      return this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > this.priceUnformatted ? productPrice(this.selectedVariant.compare_at_price) : null
    },
    badges () {
        return this.quickViewProduct.tags.filter(tag => tag.startsWith('badge-')).map(tag => tag.replace('badge-', ''))
    }
  },
  watch: {
    showQuickView(isVisible) {
      if (isVisible) {
        this.isQuickViewReady = false
        this.$nextTick(() => {
          this.setupIntersectionObserver()
          setTimeout(() => {
            this.isQuickViewReady = true
          }, 100)
        })
      }
    },
    quickViewProduct: {
      handler() {
        this.selectedVariant = this.quickViewProduct.variants.find(variant => variant.available)
      },
    },
    selectedVariant: {
      handler(newVariant, oldVariant) {
        if (this.quickViewProduct.isCombinedListingsProduct && 
            (oldVariant != null &&
            (newVariant.id !== oldVariant?.id))
        ) {
            this.updateYotpo()
        }
      }
    },
    isMainAddActionsVisible(newVal) {
      console.log('isMainAddActionsVisible changed:', newVal)
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    ...mapActions(useCollectionStore, ['hideQuickView']),

    updateYotpo () {
      const starsWidget = this.$refs.yotpoStars?.$el

      if (starsWidget) {
        const instanceId = starsWidget.getAttribute('data-yotpo-instance-id')
        starsWidget.innerHTML = `<div class="yotpo-widget-instance" data-yotpo-instance-id="${instanceId}" data-yotpo-product-id="${this.yotpoProduct.id}" data-yotpo-section-id="product"></div>`
      }

      const { yotpoWidgetsContainer } = window
      if (yotpoWidgetsContainer) {
        setTimeout(async () => { await yotpoWidgetsContainer.initWidgets() }, 300)
      }
    },
    
    setupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect()
      }

      const options = {
        root: null,
        rootMargin: '20% 0px 0px 0px',
        threshold: 0.8
      }

      this.observer = new IntersectionObserver((entries) => {
        this.isMainAddActionsVisible = entries[0].isIntersecting
      }, options)

      this.$nextTick(() => {
        if (this.$refs.mainAddActions) {
          this.observer.observe(this.$refs.mainAddActions.$el)
        }
      })
    }
  }
}
</script>

